import { isRequestError } from 'utils/requestError';
import { Button, useForm } from 'modules';
import { Icon } from 'components/icon';
import React from 'react';
import { PopupWraper } from '../../../components/popup';
import { InputFileXlsx } from 'components/input/file/InputFileXlsx';
import * as XLSX from 'xlsx';
import { Loading } from 'components';
import { CellContext, ColumnDef } from '@tanstack/react-table';
import TableListDataEdit from 'components/Table-list-edit-data';
import { useCreateMultiUserMutation } from 'services/user/mutation/useCreateMultiUserMutation';
import { getUserRoles } from 'utils/helpers/auth';
import toast from 'react-hot-toast';
import { set } from 'lodash';
import { normalizedPhoneNumber } from 'utils/helpers/helpers';
import { Roles, SubRolesOfAgency } from 'configs/roles';
import { checkValidRole, checkValidActionRoleAdmin } from 'utils/helpers';
import type { CreateUserDto } from 'services/openapi';
import ExcelJS from 'exceljs';
import saveAs from 'file-saver';
import { formatColumn, formatRowData } from 'configs/cell_xlsx';
import { Actions } from 'configs/actions';
import {
  exportHeadersCreateMultiUser,
  getUserRoleOptions,
} from '../list/configs';

interface CreateUserProps {
  onClose: () => void;
  forceUpdateTable: () => void;
  companyInfo?: any[];
}

const CreateMultiUser = ({
  onClose,
  forceUpdateTable,
  companyInfo,
}: CreateUserProps) => {
  const [dataImport, setDataImport] = React.useState<any[]>([]);
  const userRoleOptions = getUserRoleOptions(getUserRoles());
  const importRoleOptions = userRoleOptions.map(({ label }) => ({
    label,
    value: label,
  }));

  const { mutateAsync, isLoading } = useCreateMultiUserMutation();

  const { handleSubmit } = useForm({
    structure: [],
    onSubmit: async (values) => {
      try {
        const normalizedValues = dataImport.map((item, index) => {
          const selectedRole = userRoleOptions.find(
            (option) => option.label === item['Vai trò']?.trim(),
          )?.value;
          if (!selectedRole) {
            throw new Error(
              `Dòng ${index + 2}: Vai trò không hợp lệ hoặc bạn không có quyền chọn vai trò này`,
            );
          }
          const isSubRole = (
            Object.values(SubRolesOfAgency) as string[]
          ).includes(selectedRole);
          return {
            phoneNumber: item['Tài khoản'],
            fullName: item['Tên tài khoản'],
            email: item['Email'],
            citizenIdentityCardNo: item['CCCD/MST'],
            role: (isSubRole
              ? Roles.AGENCY
              : selectedRole) as CreateUserDto['role'],
            compId: item['Doanh nghiệp/HTX'],
            isActive: item['Trạng thái'] === 'Hoạt động',
            bidvVaPayerId: item['Tiểu khoản BIDV'],
            objectType: (isSubRole
              ? selectedRole
              : SubRolesOfAgency.STAFF) as CreateUserDto['objectType'],
          };
        });
        const res = await mutateAsync({ users: normalizedValues });
        // Show success message
        toast.success('Xử lý thành công. Vui lòng kiểm tra lại thông tin');
        // Reset the form or perform any other actions
        setDataImport((prevData) => {
          const newData = [...prevData];
          newData.forEach((item) => {
            const user = res.data.find(
              (user: any) =>
                user.phoneNumber?.replace('+84', '') ===
                item['Tài khoản']?.replace('+84', '0').slice(1),
            );
            set(item, 'statusUpdate', user?.message);
          });
          return newData;
        });
        // resetForm();
        forceUpdateTable();
        // onClose();
      } catch (error: any) {
        if (!isRequestError(error)) {
          toast.error(error.message || 'Không thể tạo người dùng');
        }
      }
    },
  });

  const checkedDataCompany =
    companyInfo?.map((item) => ({
      label: `${item.compName}`,
      value: item.compId,
    })) || [];

  const downloadTemplateCreateMultiUser = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Sheet1');
    const headerColumn = exportHeadersCreateMultiUser.map((header) => {
      return {
        header: header,
        key: header,
        width: 40,
      };
    });
    worksheet.columns = headerColumn;
    const firstRow = worksheet.getRow(1);
    firstRow.eachCell((cell) => formatColumn(cell));
    const row = worksheet.addRow([
      '1',
      '0987654321',
      'Nguyễn Văn A',
      '123456789',
      'BIDV_VA',
      'demo@gmail.com',
      'Nhân viên',
      '',
      'Hoạt động',
    ]);
    row.eachCell((cell) => formatRowData(cell));

    // Thêm dropdown vào ô G2
    // Lấy danh sách key từ enum RoleTypesAll và tạo danh sách cho dropdown
    const roleOptions = importRoleOptions
      .map((option) => option.label)
      .join(',');

    // Thêm dropdown vào ô G2
    worksheet.getCell('G2').dataValidation = {
      type: 'list',
      allowBlank: true,
      formulae: [`"${roleOptions}"`], // danh sách chọn
      showErrorMessage: true,
      errorStyle: 'error',
      errorTitle: 'Sai lựa chọn',
      error: 'Vui lòng chọn một giá trị từ danh sách.',
    };

    // Thêm dropdown vào ô J2
    worksheet.getCell('H2').dataValidation = {
      type: 'list',
      allowBlank: true,
      formulae: [`"${checkedDataCompany.map((item) => item.label).join(',')}"`], // danh sách chọn
      // formulae: ['""'], // danh sách chọn
      showErrorMessage: true,
      errorStyle: 'error',
      errorTitle: 'Sai lựa chọn',
      error: 'Vui lòng chọn một giá trị từ danh sách.',
    };
    // Thêm dropdown vào ô I2
    worksheet.getCell('I2').dataValidation = {
      type: 'list',
      allowBlank: true,
      formulae: [`"Hoạt động, Không hoạt động"`], // danh sách chọn
      // formulae: ['""'], // danh sách chọn
      showErrorMessage: true,
      errorStyle: 'error',
      errorTitle: 'Sai lựa chọn',
      error: 'Vui lòng chọn một giá trị từ danh sách.',
    };

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const now = new Date();
    saveAs(
      blob,
      `Template_tao_nguoi_dung_${now
        .toLocaleTimeString('vn-VN', { hour12: false })
        .replace(/:/g, '_')}_${now
        .toLocaleDateString('vi-VN', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        })
        .replace(/\//g, '_')}.xlsx`,
    );
  };

  const handleRemoveRow = (info: CellContext<any, unknown>) => {
    setDataImport((prevData) => {
      const newData = [...prevData];
      newData.splice(info.row.index, 1);
      return newData;
    });
  };

  const columnsDefined: ColumnDef<any>[] = [
    {
      accessorKey: 'Tài khoản',
      header: 'Tài khoản',
      cell: (info) => normalizedPhoneNumber(String(info.getValue())),
      meta: {
        editable: true,
      },
    },
    {
      accessorKey: 'Tên tài khoản',
      header: 'Tên tài khoản',
      meta: {
        editable: true,
      },
    },
    {
      accessorKey: 'CCCD/MST',
      header: 'CCCD',
      meta: {
        editable: true,
      },
    },
    {
      accessorKey: 'Tiểu khoản BIDV',
      header: 'Tiểu khoản BIDV',
      meta: {
        editable: true,
      },
    },
    {
      accessorKey: 'Vai trò',
      header: 'Vai trò',
      cell: (info) => {
        const value = info.getValue();
        const option = importRoleOptions.find((opt) => opt.value === value);
        return option ? option.label : value;
      },
      meta: {
        editable: true,
        options: importRoleOptions,
      },
    },
    {
      accessorKey: 'Doanh nghiệp/HTX',
      header: 'Doanh nghiệp/HTX',
      cell: (info) => {
        const value = info.getValue();
        const option = checkedDataCompany.find((opt) => opt.value === value);
        return (
          <div style={{ width: 150 }}>
            {option ? option.label : String(value)}
          </div>
        );
      },
      meta: {
        editable: true,
        options: checkedDataCompany,
      },
    },
    {
      accessorKey: 'Email',
      header: 'Email',
      meta: {
        editable: true,
      },
    },
    {
      accessorKey: 'Trạng thái',
      header: 'Trạng thái',
      cell: (info) => {
        const value = info.getValue();
        const option = [
          { label: 'Hoạt động', value: 'Hoạt động' },
          { label: 'Không hoạt động', value: 'Không hoạt động' },
        ].find((opt) => opt.value === value);
        return option ? option.label : value;
      },
      meta: {
        editable: true,
        options: [
          {
            label: 'Hoạt động',
            value: 'Hoạt động',
          },
          {
            label: 'Không hoạt động',
            value: 'Không hoạt động',
          },
        ],
      },
    },
    {
      accessorKey: 'Thao tác',
      header: 'Thao tác',
      cell: (info: CellContext<any, unknown>) => {
        return (
          <div
            className='btnRemove'
            style={{
              cursor: 'pointer',
            }}
            onClick={() => handleRemoveRow(info)}
          >
            <Icon.Trash />
          </div>
        );
      },
    },
    {
      accessorKey: 'statusUpdate',
      header: 'Trạng thái cập nhật',
      cell: (info) => {
        const value = info.getValue();
        if (!value) return '';
        if (value?.toString()?.trim()?.toLowerCase() == 'thành công') {
          return <div style={{ color: 'green' }}>{String(value)}</div>;
        }
        return <div style={{ color: 'red' }}>{String(value)}</div>;
      },
    },
  ];

  const handleUploadFile = (event: any) => {
    try {
      const file = event?.[0];
      if (!(file instanceof Blob)) return; //
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const result = e.target?.result;
          if (!result) return;

          const data = new Uint8Array(result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });

          const sheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[sheetName];

          const jsonData = XLSX.utils.sheet_to_json(sheet);

          const dataHandle: any = jsonData.map((item: any) => {
            const newItem = { ...item };
            if (newItem['Tài khoản']) {
              newItem['Tài khoản'] = normalizedPhoneNumber(
                newItem['Tài khoản'],
              );
            }
            if (newItem['Doanh nghiệp/HTX']) {
              const compId = checkedDataCompany.find(
                (item) => item.label === newItem['Doanh nghiệp/HTX'],
              )?.value;
              newItem['Doanh nghiệp/HTX'] = compId;
            }
            if (newItem['Vai trò']) {
              newItem['Vai trò'] = newItem['Vai trò'].trim();
            }
            if (newItem['Trạng thái']) {
              newItem['Trạng thái'] = newItem['Trạng thái'].trim();
            }
            if (newItem['CCCD/MST']) {
              newItem['CCCD/MST'] = newItem['CCCD/MST'].toString().trim();
            }
            if (newItem['Tiểu khoản BIDV']) {
              newItem['Tiểu khoản BIDV'] = newItem['Tiểu khoản BIDV']
                .toString()
                .trim();
            }
            if (newItem['Email']) {
              newItem['Email'] = newItem['Email'].toString().trim();
            }
            if (newItem['Tên tài khoản']) {
              newItem['Tên tài khoản'] = newItem['Tên tài khoản']
                .toString()
                .trim();
            }
            return newItem;
          });
          setDataImport(dataHandle);
        } catch (error) {
          console.log('error', error);
          toast.error('Tệp không đúng định dạng. Vui lòng kiểm tra lại');
        }
      };

      reader.readAsArrayBuffer(file);
    } catch (error) {
      console.log('error', error);
      toast.error('Tệp không đúng định dạng. Vui lòng kiểm tra lại');
    }
  };

  return (
    <div className='wrap-pop-up-create-user'>
      <PopupWraper center title='Tạo mới tài khoản' onClose={() => onClose()}>
        <Loading isLoading={isLoading}>
          <div className='container-fluid'>
            <div className='row wrap-form-create-user mb20'>
              <div className='col-5 mb15'>
                <InputFileXlsx
                  name='Tải tệp'
                  onChange={(event) => {
                    handleUploadFile(event);
                  }}
                  onTouched={() => {}}
                />
              </div>
              <div className='col-6 mb15'>
                {(checkValidActionRoleAdmin(
                  Actions.ManageUser_CreateMultipleUser,
                ) ||
                  checkValidRole([Roles.MC])) && (
                  <Button
                    label={'Download Template'}
                    onClick={() => downloadTemplateCreateMultiUser()}
                    buttonType='info'
                  />
                )}
              </div>
            </div>
          </div>
          <TableListDataEdit<any>
            columns={columnsDefined as ColumnDef<any>[]}
            data={dataImport}
            setData={setDataImport}
            orderColumn
          />

          <div className='wrap-button-create-user'>
            <Button
              label='Tạo mới'
              type='submit'
              onClick={handleSubmit}
              buttonType='success'
              disabled={isLoading}
            />
            <Button
              label='Trở về'
              buttonType='grey'
              onClick={() => onClose()}
            />
          </div>
        </Loading>
      </PopupWraper>
    </div>
  );
};

export default CreateMultiUser;
